import Text from "common/Typography/Text";
import CopyToClipboardInput from "common/CopyToClipboard/CopyToClipboardInput";
import React, { CSSProperties } from "react";
import { useTheme } from "styled-components";
import {
  useWhitelistIPs as useSharedWhitelistIPs,
  useWhitelistIPsComponent,
} from "@shared/features/configuration/hooks";
import { GetWhitelistIpsRequest } from "@shared/types";
import { CREATE_ACCOUNT_COPY_IPS_BUTTON_ID } from "testIds.ts";
import NewLookWhitelistIPs from "features/portfolio/CreateAccount/NewLook/NewLookWhitelistIPs";

interface WhitelistIPsProps {
  style?: CSSProperties;
  onCopyClick?: () => void;
  showWarning?: boolean;
  args?: GetWhitelistIpsRequest;
  exchange?: string;
}

const WhitelistIPs = ({
  style,
  onCopyClick,
  showWarning,
  args,
  exchange,
}: WhitelistIPsProps) => {
  const theme = useTheme();
  const { important, mainMessage, copyText, notificationPrefix } =
    useSharedWhitelistIPs({
      args,
      exchange,
    });

  return (
    <div style={style}>
      <Text $size={"S"} style={{ color: theme.colors.midDark }}>
        <span style={{ fontWeight: theme.typography.fontWeights.MEDIUM }}>
          {important}
        </span>{" "}
        {mainMessage}
      </Text>
      <CopyToClipboardInput
        text={copyText}
        notificationPrefix={notificationPrefix}
        style={{ marginTop: 12 }}
        onCopyClick={onCopyClick}
        dataTestId={CREATE_ACCOUNT_COPY_IPS_BUTTON_ID}
      />
      {showWarning && (
        <Text type="warning" style={{ marginTop: 4 }}>
          Please make sure to copy the IP addresses before proceeding{" "}
        </Text>
      )}
    </div>
  );
};

export const useWhitelistIPs = (
  exchange?: string,
  args?: GetWhitelistIpsRequest
) => {
  const {
    checkAllowSave,
    showCopyIPsWarning,
    setShowCopyIPsWarning,
    setCopyIPsClicked,
    isSupportedExchange,
  } = useWhitelistIPsComponent(exchange);
  const { isNewLook } = useTheme();

  const onCopyClick = () => {
    if (showCopyIPsWarning) setShowCopyIPsWarning(false);
    setCopyIPsClicked(true);
  };

  const getWhitelistIPs = () => {
    if (!isSupportedExchange) return null;
    if (isNewLook) {
      return (
        <NewLookWhitelistIPs
          onCopyClick={onCopyClick}
          showWarning={showCopyIPsWarning}
          args={args}
          exchange={exchange}
        />
      );
    }
    return (
      <WhitelistIPs
        onCopyClick={onCopyClick}
        showWarning={showCopyIPsWarning}
        style={{ marginTop: 12, marginBottom: 16 }}
        args={args}
        exchange={exchange}
      />
    );
  };

  const whitelistIPs = getWhitelistIPs();

  return {
    whitelistIPs,
    checkAllowSave,
  };
};
